import { forwardRef, useState } from "react";
import { Collapse, IconButton, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { mergeWith } from "lodash";

import { colors } from "theme";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: 24,
    boxSizing: "border-box",
    borderWidth: 1,
    borderStyle: "solid",
    flexDirection: ({ isFullWidthText }: { isFullWidthText: boolean }) =>
      isFullWidthText ? "column" : "row",
    "& a": {
      color: `${theme.palette.secondary.main} !important`,
    },
    "& a:not(.MuiLink-underlineAlways)": {
      textDecoration: "none",
    },
  },
  icon: {
    fontSize: 24,
    padding: 0,
    marginRight: 16,
  },
  message: {
    padding: 0,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "23px",
    letterSpacing: 0.5,
    color: colors.nero[300],
    overflow: "inherit",
  },
  action: {
    marginRight: 0,
    marginTop: ({ isFullWidthText }: { isFullWidthText: boolean }) =>
      isFullWidthText ? 16 : 0,
    alignItems: "center",
    "& > button": {
      padding: 0,
      color: colors.nero[300],
    },
  },
  outlinedError: {
    backgroundColor: "#FAE9E9",
    borderColor: colors.semantic.error,
  },
  outlinedWarning: {
    backgroundColor: "#FCF1E8",
    borderColor: colors.semantic.warning,

    "& .MuiAlert-icon": {
      color: colors.semantic.warning,
    },
  },
  outlinedSuccess: {
    backgroundColor: "#EAF4ED",
    borderColor: colors.semantic.positive,

    "& .MuiAlert-icon": {
      color: colors.semantic.positive,
    },
  },
  outlinedInfo: {
    backgroundColor: theme.palette.secondary.light,
    borderColor: colors.semantic.info,
  },
}));

const Alert = forwardRef(
  (
    {
      classes = {},
      isFullWidthText,
      noDismiss,
      ...otherProps
    }: AlertProps & {
      isFullWidthText?: boolean;
      noDismiss?: boolean;
      onDismiss?: Function;
    },
    ref: any
  ) => {
    const baseClasses = useStyles({
      isFullWidthText: Boolean(isFullWidthText),
    });
    const alertClasses = mergeWith(
      baseClasses,
      classes,
      (class1: string, class2: string) => `${class1} ${class2}`
    );
    const [open, setOpen] = useState(true);

    return (
      <Collapse in={open}>
        <MuiAlert
          ref={ref}
          action={
            !Boolean(noDismiss) ? (
              <IconButton
                onClick={() => {
                  otherProps.onDismiss?.();
                  setOpen(false);
                }}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            ) : null
          }
          variant="outlined"
          iconMapping={{
            info: <InfoIcon color="secondary" />,
            success: <SuccessIcon />,
            warning: <ErrorIcon />,
            error: <WarningIcon />,
          }}
          {...otherProps}
          classes={alertClasses}
        />
      </Collapse>
    );
  }
);

export default Alert;
